import React, { useEffect, useState } from 'react';
import { Button, Card, Badge, Nav } from 'react-bootstrap';
import ListingTable from '../../../../Components/ListingTable';
import CustomAccordion from '../../../../Components/CustomAccordion';
import DetailsPageLayout from '../../../../Components/DetailsPageLayout/DetailsPageLayout';
import Loader from '../../../../Components/Loader';
import TimeOffRequestModal from '../../../../Components/ScheduleModals/TimeOffRequestModal';
import AvailabilityRequestModal from '../../../../Components/ScheduleModals/AvailabilityRequestModal';
import RequestDetailModal from '../../../../Components/ScheduleModals/RequestDetailModal';
import CancelConfirmModal from '../../../../Components/ScheduleModals/CancelConfirmModal';
import { useScheduleState, useAccountState, getTripSettings } from '../../../../Context';
import './index.scss';
import {
    cancelScheduleRequest,
    editScheduleRequest,
    fetchAbsenceTypes,
    fetchAvailabilityRequests,
    fetchScheduleRequests,
    submitAvailabilityRequest,
    submitScheduleRequest,
} from '../../../../Context/actions/scheduleActions';

const TIME_OFF_REQUEST_TYPE_ID = 1;
const AVAILABILITY_REQUEST_TYPE_ID = 2;

const STATUS_VARIANTS = {
    pending: 'warning',
    approved: 'success',
    denied: 'danger',
};

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const Schedule = () => {
    const {
        scheduleDispatch,
        schedule: { scheduleRequests, availabilityRequests, absenceTypes, loading, pageInfo, availabilityPageInfo },
    } = useScheduleState();
    const {
        AccountsDispatch,
        tripSettings: { tripSettings },
    } = useAccountState();

    const [activeTab, setActiveTab] = useState('timeoff');
    const [collapseItem, handleCollapse] = useState({ requests: false, availability: false });

    // Modal state
    const [submitError, setSubmitError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Time Off modal
    const [showTimeOffModal, setShowTimeOffModal] = useState(false);
    const [timeOffMode, setTimeOffMode] = useState('add');
    const [timeOffEditData, setTimeOffEditData] = useState(null);

    // Availability modal
    const [showAvailModal, setShowAvailModal] = useState(false);
    const [availMode, setAvailMode] = useState('add');
    const [availEditData, setAvailEditData] = useState(null);

    // Detail modal
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // Cancel confirm
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    useEffect(() => {
        (async () => {
            await Promise.all([
                getTripSettings(AccountsDispatch),
                fetchAbsenceTypes(scheduleDispatch),
                fetchScheduleRequests(scheduleDispatch),
                fetchAvailabilityRequests(scheduleDispatch),
            ]);
        })();
    }, [AccountsDispatch, scheduleDispatch]);

    const handleItemCollapse = (e) => {
        const { id } = e.target;
        handleCollapse({ ...collapseItem, [id]: !collapseItem[id] });
    };

    // ── Open Add Modals ──
    const openAddTimeOff = () => {
        setTimeOffMode('add');
        setTimeOffEditData(null);
        setSubmitError(null);
        setShowTimeOffModal(true);
    };

    const openAddAvailability = () => {
        setAvailMode('add');
        setAvailEditData(null);
        setSubmitError(null);
        setShowAvailModal(true);
    };

    // ── Detail Modal ──
    const openDetailModal = (item) => {
        setSelectedRequest(item);
        setSubmitError(null);
        setShowDetailModal(true);
    };

    const closeDetailModal = () => {
        setShowDetailModal(false);
        setSelectedRequest(null);
        setSubmitError(null);
    };

    // ── Edit from Detail ──
    const handleEditFromDetail = () => {
        const req = selectedRequest;
        closeDetailModal();
        if (req.requestTypeID === TIME_OFF_REQUEST_TYPE_ID) {
            setTimeOffMode('edit');
            setTimeOffEditData(req);
            setSubmitError(null);
            setShowTimeOffModal(true);
        } else {
            setAvailMode('edit');
            setAvailEditData(req);
            setSubmitError(null);
            setShowAvailModal(true);
        }
    };

    // ── Submit Handlers ──
    const handleTimeOffSubmit = async (data) => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            if (timeOffMode === 'edit' && timeOffEditData) {
                await editScheduleRequest(scheduleDispatch, timeOffEditData.id, data);
            } else {
                await submitScheduleRequest(scheduleDispatch, {
                    requestTypeID: TIME_OFF_REQUEST_TYPE_ID,
                    ...data,
                });
            }
            setShowTimeOffModal(false);
        } catch (err) {
            setSubmitError(
                err?.response?.data?.message ||
                    (timeOffMode === 'edit'
                        ? 'Failed to update request.'
                        : 'Failed to submit request. Please try again.')
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleAvailSubmit = async (data) => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            if (availMode === 'edit' && availEditData) {
                await editScheduleRequest(scheduleDispatch, availEditData.id, data);
            } else {
                await submitAvailabilityRequest(scheduleDispatch, {
                    requestTypeID: AVAILABILITY_REQUEST_TYPE_ID,
                    ...data,
                });
            }
            setShowAvailModal(false);
        } catch (err) {
            setSubmitError(
                err?.response?.data?.message ||
                    (availMode === 'edit'
                        ? 'Failed to update availability request.'
                        : 'Failed to submit availability request. Please try again.')
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelRequest = async () => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            await cancelScheduleRequest(scheduleDispatch, selectedRequest.id);
            setShowCancelConfirm(false);
            closeDetailModal();
        } catch (err) {
            setSubmitError(err?.response?.data?.message || 'Failed to cancel request.');
            setShowCancelConfirm(false);
        } finally {
            setSubmitting(false);
        }
    };

    // ── Table Renderers ──
    const renderStatus = (item) => (
        <Badge variant={STATUS_VARIANTS[item.status] || 'secondary'}>
            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
        </Badge>
    );

    const renderPartialDay = (item) => (item.isPartialDay ? `${item.partialDayStart} – ${item.partialDayEnd}` : 'No');
    const renderReason = (item) => item.absenceType?.description || '—';
    const renderNotes = (item) => item.notes || '—';
    const renderSubmitted = (item) => new Date(item.createdAt).toLocaleDateString();

    const timeOffFields = [
        ['startDate', 'Start Date', true],
        ['endDate', 'End Date', true],
        ['absenceTypeID', 'Reason', false, renderReason],
        ['isPartialDay', 'Partial Day', false, renderPartialDay],
        ['notes', 'Notes', false, renderNotes],
        ['status', 'Status', false, renderStatus],
        ['createdAt', 'Submitted', true, renderSubmitted],
    ];

    const renderAvailDays = (item) => {
        if (!item.days || item.days.length === 0) return '—';
        return item.days
            .slice()
            .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
            .map((d) => DAY_LABELS[d.dayOfWeek]?.slice(0, 3))
            .join(', ');
    };

    const renderAvailHours = (item) => {
        if (!item.days || item.days.length === 0) return '—';
        const sorted = item.days.slice().sort((a, b) => a.dayOfWeek - b.dayOfWeek);
        const allSame = sorted.every((d) => d.startTime === sorted[0].startTime && d.endTime === sorted[0].endTime);
        if (allSame) return `${sorted[0].startTime} – ${sorted[0].endTime}`;
        return 'Varies';
    };

    const renderEffectiveRange = (item) => `${item.startDate} to ${item.endDate}`;

    const availabilityFields = [
        ['startDate', 'Effective Range', true, renderEffectiveRange],
        ['days', 'Days', false, renderAvailDays],
        ['days', 'Hours', false, renderAvailHours],
        ['notes', 'Notes', false, renderNotes],
        ['status', 'Status', false, renderStatus],
        ['createdAt', 'Submitted', true, renderSubmitted],
    ];

    return (
        <DetailsPageLayout title="Schedule" icon="fa fa-clock-o" textColor={tripSettings?.textColor}>
            <Card>
                <Card.Header
                    className="d-flex justify-content-between align-items-center"
                    style={{ backgroundColor: `${tripSettings?.mainColor}` }}
                >
                    <Nav variant="pills" activeKey={activeTab} onSelect={setActiveTab} className="schedule-tabs">
                        <Nav.Item>
                            <Nav.Link eventKey="timeoff">Time Off</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="availability">Availability</Nav.Link>
                        </Nav.Item>
                    </Nav>
                    {!!tripSettings?.enableDriverScheduling &&
                        (activeTab === 'timeoff' ? (
                            <Button size="sm" className="btn btn-primary" onClick={openAddTimeOff}>
                                + New Request
                            </Button>
                        ) : (
                            <Button size="sm" className="btn btn-primary" onClick={openAddAvailability}>
                                + New Request
                            </Button>
                        ))}
                </Card.Header>
                <Card.Body className="light-gray-bg">
                    {activeTab === 'timeoff' && (
                        <CustomAccordion
                            id="requests"
                            title="My Requests"
                            collapseItem={collapseItem}
                            sectionColor={tripSettings?.sectionColor}
                            handleItemCollapse={handleItemCollapse}
                        >
                            <ListingTable
                                data={scheduleRequests}
                                loading={loading}
                                pageInfo={pageInfo}
                                noSort={true}
                                mainColor={tripSettings?.mainColor}
                                fieldNames={timeOffFields}
                                onClick={openDetailModal}
                            />
                        </CustomAccordion>
                    )}
                    {activeTab === 'availability' && (
                        <CustomAccordion
                            id="availability"
                            title="My Availability Submissions"
                            collapseItem={collapseItem}
                            sectionColor={tripSettings?.sectionColor}
                            handleItemCollapse={handleItemCollapse}
                        >
                            <ListingTable
                                data={availabilityRequests}
                                loading={loading}
                                pageInfo={availabilityPageInfo}
                                noSort={true}
                                mainColor={tripSettings?.mainColor}
                                fieldNames={availabilityFields}
                                onClick={openDetailModal}
                            />
                        </CustomAccordion>
                    )}
                </Card.Body>
            </Card>
            {loading && <Loader />}

            <TimeOffRequestModal
                show={showTimeOffModal}
                onHide={() => setShowTimeOffModal(false)}
                mode={timeOffMode}
                editData={timeOffEditData}
                absenceTypes={absenceTypes}
                onSubmit={handleTimeOffSubmit}
                submitting={submitting}
                submitError={submitError}
            />

            <AvailabilityRequestModal
                show={showAvailModal}
                onHide={() => setShowAvailModal(false)}
                mode={availMode}
                editData={availEditData}
                onSubmit={handleAvailSubmit}
                submitting={submitting}
                submitError={submitError}
            />

            <RequestDetailModal
                show={showDetailModal}
                onHide={closeDetailModal}
                request={selectedRequest}
                onEdit={handleEditFromDetail}
                onCancelRequest={() => setShowCancelConfirm(true)}
                submitting={submitting}
                submitError={submitError}
            />

            <CancelConfirmModal
                show={showCancelConfirm}
                onHide={() => setShowCancelConfirm(false)}
                onConfirm={handleCancelRequest}
                submitting={submitting}
            />
        </DetailsPageLayout>
    );
};

export default Schedule;
